import { useContext, useEffect, useCallback } from 'react';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { fg } from '@confluence/feature-gating';

import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '../onboardingState/onboardingState.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '../../constants/onboarding-state/onboarding-editor-constants.ts';
import { ONBOARDING_INTENT_EXPERIENCES } from '../../constants/onboarding-state/onboarding-constants.ts';
import { useIsEstablishedUserTraitName } from '../useIsEstablishedUserTraitName.ts';
import { useExperimentalUserPersonalizationQuery } from '../useExperimentalUserPersonalizationQuery.ts';

interface SetOnboardingEligibilityProps {
	/* Onboarding state key that indicates if a user is eligible for an experiment */
	onboardingStateEligibleValue: boolean;
	/* Boolean for if the feature flag is enabled */
	featureFlagEnabled: boolean;
	/* Onboarding state key that will be set to `true`*/
	onboardingStateEligibleKey: string;
}

export const useOnboardingEligible = () => {
	const { isNewUser } = useContext(SPAViewContext);
	const { userId } = useSessionData();
	const { setOnboardingState } = useSetOnboardingState();
	const isLicensed = useIsLicensed();
	const eligibleNewUser = isLicensed && isNewUser;
	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();

	/**
	 * Skip the query for checking whether the user is an established user if:
	 * 1. The master onboarding FF is OFF
	 * 2. The user is not licensed
	 * 3. The user is not logged in (anonymous)
	 * 4. The user is not new (hasn't viewed a confluence page)
	 */
	const skipQuery = !eligibleNewUser || !fg('cc_onboarding_experience');

	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
	} = useExperimentalUserPersonalizationQuery({
		variables: {
			userId: userId!,
		},
		skip: skipQuery,
	});

	const attributes =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];
	// if we skip querying for personalization,
	// we can assume that user should be establishedUser and skip over onboarding
	const isEstablishedUser =
		skipQuery ||
		attributes.find((attr) => attr?.name === isEstablishedUserTraitName)?.value === 'true';

	const isEstablishedUserReady = !userPersonalizationQueryLoading && !userPersonalizationQueryError;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			...Object.values(EDITOR_ONBOARDING_STATE_KEYS),
			...Object.values(ONBOARDING_INTENT_EXPERIENCES),
		],
		!isLicensed || !fg('cc_onboarding_experience'),
	);

	const { editorOnboardingEligiblity, isOnboardingExperienceEligibleUser } =
		deserializeState(onboardingStateData);

	const checkAndSetOnboardingEligibility = useCallback(
		({
			onboardingStateEligibleValue,
			featureFlagEnabled,
			onboardingStateEligibleKey,
		}: SetOnboardingEligibilityProps) => {
			if (
				!isEstablishedUser &&
				isEstablishedUserReady &&
				onboardingStateData &&
				featureFlagEnabled &&
				!onboardingStateEligibleValue
			) {
				void setOnboardingState({
					key: onboardingStateEligibleKey,
					value: 'true',
				});
			}
		},
		[isEstablishedUser, onboardingStateData, setOnboardingState, isEstablishedUserReady],
	);

	// IBO Tagging
	useEffect(() => {
		checkAndSetOnboardingEligibility({
			onboardingStateEligibleValue: isOnboardingExperienceEligibleUser,
			featureFlagEnabled: true,
			onboardingStateEligibleKey: ONBOARDING_INTENT_EXPERIENCES.ONBOARDING_EXPERIENCE_ELIGIBLE,
		});
	}, [isOnboardingExperienceEligibleUser, checkAndSetOnboardingEligibility]);

	// Editor Onboarding Experiment
	useEffect(() => {
		if (userPersonalizationQueryData && onboardingStateData && fg('cc_onboarding_experience')) {
			// only check for users with Eligibility not set
			if (editorOnboardingEligiblity === null || editorOnboardingEligiblity === undefined) {
				if (isEstablishedUser) {
					// Set EDITOR_ONBOARDING_ELIGIBLE_NEW: false for established users
					void setOnboardingState({
						key: EDITOR_ONBOARDING_STATE_KEYS.EDITOR_ONBOARDING_ELIGIBLE_NEW,
						value: 'false',
					});
				} else {
					// Set EDITOR_ONBOARDING_ELIGIBLE_NEW: true for users that are new to confluence
					void setOnboardingState({
						key: EDITOR_ONBOARDING_STATE_KEYS.EDITOR_ONBOARDING_ELIGIBLE_NEW,
						value: 'true',
					});
				}
			}
		}
	}, [
		eligibleNewUser,
		isEstablishedUser,
		editorOnboardingEligiblity,
		userPersonalizationQueryData,
		onboardingStateData,
		onboardingStateLoading,
		onboardingStateError,
		setOnboardingState,
	]);

	return {
		isEstablishedUser,
		isEstablishedUserReady,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
		skipQuery,
		eligibleNewUser,
	};
};
